import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, UserX, UserCheck } from "lucide-react";
import api from "../lib/api";
import { RoleBadge } from "../components/badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const EMPTY = {
  name: "", employee_id: "", email: "", mobile: "", role: "employee", department_id: "",
  designation: "", reporting_manager_id: "", joining_date: "", password: "", can_assign_upward: false,
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [u, r, d] = await Promise.all([
      api.get("/users"), api.get("/roles"), api.get("/departments"),
    ]);
    setUsers(u.data); setRoles(r.data); setDepartments(d.data);
  };

  useEffect(() => { load().catch((e) => toast.error(e.friendlyMessage)); }, []);

  const deptName = (id) => departments.find((d) => d.id === id)?.name || "-";
  const mgrName = (id) => users.find((u) => u.id === id)?.name || "-";

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name, employee_id: u.employee_id || "", email: u.email, mobile: u.mobile || "",
      role: u.role, department_id: u.department_id || "", designation: u.designation || "",
      reporting_manager_id: u.reporting_manager_id || "", joining_date: u.joining_date || "",
      password: "", can_assign_upward: !!u.can_assign_upward,
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, department_id: form.department_id || null, reporting_manager_id: form.reporting_manager_id || null };
      if (editing) {
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", payload);
        toast.success(`User created (default password ${form.password ? "set" : "Pharma@123"})`);
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e.friendlyMessage); }
    setBusy(false);
  };

  const toggleStatus = async (u) => {
    try {
      const res = await api.patch(`/users/${u.id}/status`);
      toast.success(`${u.name} is now ${res.data.status}`);
      load();
    } catch (e) { toast.error(e.friendlyMessage); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="animate-fade-in space-y-5" data-testid="users-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Administration</div>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">User Management</h1>
        </div>
        <Button data-testid="users-add-button" onClick={openCreate} className="bg-primary font-semibold hover:bg-[#1E3A8A]">
          <Plus className="mr-1.5 h-4 w-4" /> Add User
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100/80 text-left text-xs uppercase tracking-wider text-slate-600">
                <th className="px-4 py-3 font-semibold">Employee</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Department</th>
                <th className="px-4 py-3 font-semibold">Reports To</th>
                <th className="px-4 py-3 font-semibold">Contact</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100 transition-colors hover:bg-slate-50/80">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{u.name}</div>
                    <div className="text-xs text-slate-500">{u.employee_id} - {u.designation || "No designation"} - Level {u.hierarchy_level}</div>
                  </td>
                  <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                  <td className="px-4 py-3 text-slate-600">{deptName(u.department_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{mgrName(u.reporting_manager_id)}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-slate-600">{u.email}</div>
                    <div className="font-data text-xs text-slate-400">{u.mobile || "-"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${u.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {u.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {u.role !== "owner" && (
                        <Button size="sm" variant="ghost" data-testid={`user-toggle-status-${u.id}`}
                          onClick={() => toggleStatus(u)}
                          className={u.status === "active" ? "text-rose-600 hover:text-rose-700" : "text-emerald-600 hover:text-emerald-700"}>
                          {u.status === "active" ? <UserX className="h-3.5 w-3.5" /> : <UserCheck className="h-3.5 w-3.5" />}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-form-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? `Edit ${editing.name}` : "Add New User"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Full Name *</Label>
              <Input data-testid="user-form-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Employee ID</Label>
              <Input data-testid="user-form-employee-id" value={form.employee_id} onChange={(e) => set("employee_id", e.target.value)} placeholder="EMP-010" />
            </div>
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input data-testid="user-form-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile / WhatsApp</Label>
              <Input data-testid="user-form-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+91 ..." />
            </div>
            <div className="space-y-1.5">
              <Label>Role *</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.name} value={r.name}>{r.label} (Level {r.level})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Department</Label>
              <Select value={form.department_id} onValueChange={(v) => set("department_id", v)}>
                <SelectTrigger data-testid="user-form-department"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Designation</Label>
              <Input data-testid="user-form-designation" value={form.designation} onChange={(e) => set("designation", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Reporting Manager</Label>
              <Select value={form.reporting_manager_id} onValueChange={(v) => set("reporting_manager_id", v)}>
                <SelectTrigger data-testid="user-form-manager"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  {users.filter((u) => u.id !== editing?.id && u.role !== "employee").map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Joining Date</Label>
              <Input data-testid="user-form-joining-date" type="date" value={form.joining_date} onChange={(e) => set("joining_date", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{editing ? "Reset Password (leave blank to keep)" : "Password (default Pharma@123)"}</Label>
              <Input data-testid="user-form-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5 sm:col-span-2">
              <div>
                <div className="text-sm font-semibold text-slate-800">Allow assigning tasks to higher levels</div>
                <div className="text-xs text-slate-500">Exception to the hierarchy rule for this user</div>
              </div>
              <Switch data-testid="user-form-can-assign-upward" checked={form.can_assign_upward}
                onCheckedChange={(v) => set("can_assign_upward", v)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="user-form-save" onClick={save} disabled={busy || !form.name || !form.email}
              className="bg-primary hover:bg-[#1E3A8A]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
